import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeCard } from "@/components/QRCodeCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QrCode } from "lucide-react";
import type { Staff, Business } from "@shared/schema";

export default function QRCodes() {
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");

  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const { data: staffList, isLoading } = useQuery<Staff[]>({
    queryKey: ["/api/staff"],
  });

  // Set initial selection when staff loads
  useEffect(() => {
    if (staffList && staffList.length > 0 && !selectedStaffId) {
      setSelectedStaffId(staffList[0].id);
    }
  }, [staffList, selectedStaffId]);

  const selectedStaff = staffList?.find((s) => s.id === selectedStaffId);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold">QR Codes</h1>
        <p className="text-muted-foreground mt-1">
          Generate and print QR codes for your staff members
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Generate QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Staff Member</label>
              {isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                  <SelectTrigger data-testid="select-staff-qr">
                    <SelectValue placeholder="Choose staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList?.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="bg-muted/50 rounded-md p-4">
              <h3 className="font-medium mb-2">How to use QR codes</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>1. Print and display at your service location</li>
                <li>2. Include on invoices and receipts</li>
                <li>3. Give to technicians for on-site reviews</li>
                <li>4. Add to vehicle magnets or door hangers</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          {selectedStaff && business ? (
            <QRCodeCard
              businessName={business.name}
              staffName={selectedStaff.name}
              url={`${window.location.origin}/r/${business.slug}/${selectedStaff.slug}`}
              brandColor={business.brandColor || "#3B82F6"}
            />
          ) : (
            <Skeleton className="h-80 w-72" />
          )}
        </div>
      </div>
    </div>
  );
}
